import React, { useState } from 'react';
import InsightCard from './InsightCard';
import { getSWOTCounts } from '../utils/evaluationScoreUtils';
import '../styles/components/FeedbackBySection.css';

interface SwotItem {
  id: number;
  finding: string;
  swot_type_id: number;
  swot_type?: string;
}

interface SectionInsight {
  section_name: string;
  strengths: SwotItem[];
  weaknesses: SwotItem[];
  opportunities: SwotItem[];
}

interface FeedbackBySectionProps {
  sectionInsights: SectionInsight[];
}

const FeedbackBySection: React.FC<FeedbackBySectionProps> = ({ sectionInsights }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const toggleSection = (sectionName: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionName)) {
        newSet.delete(sectionName);
      } else {
        newSet.add(sectionName);
      }
      return newSet;
    });
  };

  const getFeedbackItems = (section: SectionInsight) => {
    const items = [
      ...section.strengths.map(item => ({ ...item, type: 'strength' as const })),
      ...section.weaknesses.map(item => ({ ...item, type: 'improvement' as const })),
      ...section.opportunities.map(item => ({ ...item, type: 'improvement' as const }))
    ];
    return items;
  };

  const getSectionSWOTCounts = (section: SectionInsight) => {
    const allSwotItems = [
      ...section.strengths.map(item => ({ swot_type_id: 1 })), // Strength
      ...section.weaknesses.map(item => ({ swot_type_id: 2 })), // Weakness
      ...section.opportunities.map(item => ({ swot_type_id: 3 })) // Opportunity
    ];
    return getSWOTCounts(allSwotItems);
  };

  return (
    <div className="feedback-by-section">
      {sectionInsights.map((section, index) => {
        const feedbackItems = getFeedbackItems(section);
        const strengthCount = section.strengths.length;
        const improvementCount = section.weaknesses.length + section.opportunities.length;
        const isExpanded = expandedSections.has(section.section_name);

        return (
          <div key={index} className="feedback-section">
            <div 
              className="feedback-section-header"
              onClick={() => toggleSection(section.section_name)}
            >
              <div className="section-title">
                <h3>{section.section_name}</h3>
              </div>
              {(() => {
                const counts = getSectionSWOTCounts(section);
                const hasAnyCounts = counts.strength > 0 || counts.opportunity > 0 || counts.weakness > 0;
                
                return hasAnyCounts ? (
                  <div className="feedback-section-swot-counts">
                    <span className="swot-label">SWOT</span>
                    {counts.strength > 0 && (
                      <span className="swot-count-badge theme-success">
                        {counts.strength}
                      </span>
                    )}
                    {counts.opportunity > 0 && (
                      <span className="swot-count-badge theme-warning">
                        {counts.opportunity}
                      </span>
                    )}
                    {counts.weakness > 0 && (
                      <span className="swot-count-badge theme-danger">
                        {counts.weakness}
                      </span>
                    )}
                  </div>
                ) : null;
              })()}
              <div className="section-toggle">
                <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
              </div>
            </div>

            {isExpanded && (
              <div className="feedback-cards-container">
                <div className="feedback-cards-row">
                  {section.strengths.length > 0 && (
                    <div className="feedback-card">
                      <InsightCard 
                        swotItems={section.strengths.map(item => ({ ...item, swot_type_id: 1 }))} 
                        title="Strengths"
                      />
                    </div>
                  )}
                  {(section.weaknesses.length > 0 || section.opportunities.length > 0) && (
                    <div className="feedback-card">
                      <InsightCard 
                        swotItems={[
                          ...section.weaknesses.map(item => ({ ...item, swot_type_id: 2 })),
                          ...section.opportunities.map(item => ({ ...item, swot_type_id: 3 }))
                        ]} 
                        title="Improvements"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FeedbackBySection; 